/**
 * <s-carousel> — Horizontal scroll-snap carousel driven by remote arrow buttons.
 *
 * Commands (dispatched via commandfor/command on any button, inside or outside the element):
 *   --prev — Scroll back by one slide.
 *   --next — Scroll forward by one slide.
 *
 * Required child markup:
 *   [data-carousel-track] — The scrollable list; its children are the slides.
 *
 * Arrow buttons pointing at this carousel (commandfor="<id>") get `disabled`
 * synced at the start and end of the track.
 */
class SCarousel extends HTMLElement {
  #track;
  #ac;

  connectedCallback() {
    this.#track = this.querySelector('[data-carousel-track]');
    if (!this.#track) return;

    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.addEventListener('command', (e) => {
      if (e.command === '--next') this.#scrollBy(1);
      else if (e.command === '--prev') this.#scrollBy(-1);
    }, { signal });

    this.#track.addEventListener('scroll', () => this.#sync(), { passive: true, signal });
    window.addEventListener('resize', () => this.#sync(), { passive: true, signal });
    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get #buttons() {
    if (!this.id) return [];
    return Array.from(this.ownerDocument.querySelectorAll(`[commandfor="${CSS.escape(this.id)}"]`));
  }

  #scrollBy(direction) {
    const slide = this.#track.firstElementChild;
    if (!slide) return;
    const gap = parseFloat(getComputedStyle(this.#track).columnGap) || 0;
    const step = slide.getBoundingClientRect().width + gap;
    const rtl = getComputedStyle(this.#track).direction === 'rtl';
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#track.scrollBy({ left: step * direction * (rtl ? -1 : 1), behavior: reduce ? 'auto' : 'smooth' });
  }

  #sync() {
    const track = this.#track;
    const position = Math.abs(track.scrollLeft);
    const max = track.scrollWidth - track.clientWidth - 2;
    for (const button of this.#buttons) {
      if (button.getAttribute('command') === '--prev') button.disabled = position <= 2;
      if (button.getAttribute('command') === '--next') button.disabled = position >= max;
    }
  }
}

if (!customElements.get('s-carousel')) customElements.define('s-carousel', SCarousel);
